import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { BsCaretDownFill, BsCaretRightFill } from 'react-icons/bs';

import { copyToClipboard, getDisplayName } from './utils';
import { clusterColor } from './utils';
import { Button, Form, Row } from 'react-bootstrap';
import { FaArrowLeft, FaCopy, FaFilePdf } from 'react-icons/fa';
import { useAppState } from '../AppContext';
import Highlighter from 'react-highlight-words';
import RelatedSnippetsList from './RelatedSnippetList';
import RelatedDocumentsList from './RelatedDocumentsList';
import { GrExpand, GrContract } from 'react-icons/gr';

import './DocumentList.css';
import './CustomButton.css';

const d3 = Object.assign({}, require('d3-scale'), require('d3-array'));

const Snippet = ({ document, snip, highlightSnippet }) => {
  let [
    { selectedSnippet, searchString, documentResults, showRelatedSnippets },
    dispatch
  ] = useAppState();

  return (
    <li style={{ paddingTop: '0.75em' }}>
      <Highlighter
        textToHighlight={snip.text}
        searchWords={searchString.split(' ')}
        highlightStyle={{ backgroundColor: '#ffd54f' }}
        style={{
          backgroundColor:
            snip.id === selectedSnippet ? 'rgb(232, 242, 252)' : null
        }}
      />
      &nbsp;{' '}
      <Button
        onClick={() => copyToClipboard(snip.text)}
        variant="light"
        size="sm"
      >
        <FaCopy />
      </Button>
      <br />
      <Button
        onClick={() => highlightSnippet(snip, document)}
        variant="custom"
        size="sm"
      >
        <FaArrowLeft color={snip.id === selectedSnippet ? 'gold' : 'black'} />{' '}
        View
      </Button>{' '}
      <Button
        onClick={() =>
          dispatch({
            type: 'SET_SHOW_RELATED_SNIPPETS',
            snippetId: snip.id,
            value: !showRelatedSnippets[snip.id]
          })
        }
        variant="custom"
        size="sm"
      >
        {showRelatedSnippets[snip.id] ? (
          <>
            <GrContract /> Hide
          </>
        ) : (
          <>
            <GrExpand /> Show
          </>
        )}{' '}
        related
      </Button>
      {showRelatedSnippets[snip.id] && (
        <RelatedSnippetsList
          document={document}
          snippet_id={snip.id}
          matchingDocumentIDs={documentResults.map(
            (d) => d.id
          )} /* TODO: filter this correctly */
          highlightSnippet={highlightSnippet}
        />
      )}
    </li>
  );
};

Snippet.propTypes = {
  document: PropTypes.object.isRequired,
  snip: PropTypes.shape({ id: PropTypes.number, text: PropTypes.string })
    .isRequired,
  highlightSnippet: PropTypes.func.isRequired
};

const DocumentLi = ({ document, highlightSnippet, documentNumber }) => {
  let [
    {
      selectedDocument,
      documentResults,
      showRelatedDocs,
      isExpandedDocumentState
    },
    dispatch
  ] = useAppState();

  return (
    <div style={{ paddingTop: '5px' }}>
      <span style={{ marginLeft: '-1em' }}>
        {documentResults &&
          (isExpandedDocumentState[document.id] ? (
            <BsCaretDownFill
              onClick={(ev) =>
                dispatch({
                  type: 'SET_IS_EXPANDED_DOCUMENT_STATE',
                  isExpandedDocumentState: {
                    ...isExpandedDocumentState,
                    [document.id]: false
                  }
                })
              }

              /* color={clusterColor(cluster.id)}*/
            />
          ) : (
            <BsCaretRightFill
              onClick={(ev) =>
                dispatch({
                  type: 'SET_IS_EXPANDED_DOCUMENT_STATE',
                  isExpandedDocumentState: {
                    ...isExpandedDocumentState,
                    [document.id]: true
                  }
                })
              }
              /* color={clusterColor(cluster.id)} */
            />
          ))}{' '}
        <span>{documentNumber}.</span>{' '}
      </span>
      <span style={{ fontWeight: 'bold' }}>
        {document && getDisplayName(document)}
      </span>
      <br />
      <Button
        onClick={() => highlightSnippet(null, document)}
        size="sm"
        variant={'custom'}
      >
        <FaArrowLeft
          color={document.id === selectedDocument.id ? 'gold' : 'black'}
        />{' '}
        View
      </Button>{' '}
      <a
        href={`/api/uploads/${document.id}/${document.filename}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        <FaFilePdf title={'Open PDF of this document'} color="grey" />
      </a>
      &nbsp;
      <Button
        variant="custom"
        size="sm"
        onClick={() =>
          dispatch({
            type: 'SET_SHOW_RELATED_DOCS',
            documentId: document.id,
            value: !showRelatedDocs[document.id]
          })
        }
      >
        {showRelatedDocs[document.id] ? (
          <>
            <GrContract /> Hide
          </>
        ) : (
          <>
            <GrExpand /> Show
          </>
        )}{' '}
        related
      </Button>
      &nbsp; &nbsp;
      {showRelatedDocs[document.id] && (
        <RelatedDocumentsList
          document_id={document.id}
          highlightSnippet={highlightSnippet}
        />
      )}
      {documentResults &&
        isExpandedDocumentState[document.id] &&
        documentResults.filter((d) => d.id === document.id).length > 0 && (
          <ol>
            {documentResults
              .filter((d) => d.id === document.id)[0]
              .snippets.map((snip, i) => (
                <Snippet
                  document={document}
                  highlightSnippet={highlightSnippet}
                  snip={snip}
                  key={snip.id}
                />
              ))}
          </ol>
        )}
    </div>
  );
};

DocumentLi.propTypes = {
  document: PropTypes.object.isRequired,
  highlightSnippet: PropTypes.func.isRequired,
  documentNumber: PropTypes.number.isRequired
};

const Cluster = ({
  idsInTable,
  cluster,
  highlightSnippet,
  resultDocumentIds,
  barX,
  clusterBarLength
}) => {
  let [
    {
      documents: allDocuments,
      selectedCluster,
      mousedOverCluster,
      documentResults,
      selectedDocument,
      documentCluster,
      clusterLabels,
      isExpandedState
    },
    dispatch
  ] = useAppState();

  const all_docs_in_cluster = cluster.members.map((d) => d.document_id);
  const matching_docs_in_cluster = cluster.members
    .map((d) => d.document_id)
    .filter((doc_id) => resultDocumentIds.includes(doc_id));

  const doc_ids = (documentResults && documentResults.length > 0
    ? matching_docs_in_cluster
    : all_docs_in_cluster
  ).filter((doc_id) => idsInTable.includes(+doc_id));

  if (doc_ids.length === 0) {
    return null;
  }

  const barWidth = barX(cluster.members.length);
  const highlightBarWidth = barX(matching_docs_in_cluster.length);
  const paddingLeft = 40;

  return (
    <React.Fragment key={cluster.index}>
      <h3
        id={`cluster-${cluster.id}`}
        style={{
          fontWeight: selectedCluster === cluster.id ? 'bold' : 'normal',
          fontSize: mousedOverCluster === cluster.id ? '15pt' : '13pt',
          fontStyle: mousedOverCluster === cluster.id ? 'italic' : 'normal'
        }}
      >
        <svg width={clusterBarLength + paddingLeft + 600} height={30}>
          {isExpandedState[cluster.id] ? (
            <BsCaretDownFill
              onClick={(ev) =>
                dispatch({
                  type: 'SET_IS_EXPANDED_STATE',
                  isExpandedState: {
                    ...isExpandedState,
                    [cluster.id]: false
                  }
                })
              }
              color={clusterColor(cluster)}
            />
          ) : (
            <BsCaretRightFill
              onClick={(ev) =>
                dispatch({
                  type: 'SET_IS_EXPANDED_STATE',
                  isExpandedState: { ...isExpandedState, [cluster.id]: true }
                })
              }
              color={clusterColor(cluster)}
              title={'Expand to show documents in cluster'}
            />
          )}

          <g transform={'translate(30, 0)'}>
            <rect
              x={0}
              y={0}
              width={20}
              height={20}
              fill={clusterColor(cluster)}
              opacity={
                documentResults &&
                (!matching_docs_in_cluster ||
                  matching_docs_in_cluster.length === 0)
                  ? 0.2
                  : 1
              }
              onClick={(ev) =>
                dispatch({
                  type: 'SET_SELECTED_CLUSTER',
                  selectedCluster: cluster.id
                })
              }
              className="cluster-color-rect"
            >
              <title>Click to highlight cluster on scatterplot</title>
            </rect>

            {/* Bar for total count */}
            <rect
              x={paddingLeft}
              y={0}
              width={barWidth}
              height={20}
              fill={'lightgrey'}
              opacity={
                documentResults &&
                (!matching_docs_in_cluster ||
                  matching_docs_in_cluster.length === 0)
                  ? 0.2
                  : 1
              }
              onClick={(ev) =>
                dispatch({
                  type: 'SET_SELECTED_CLUSTER',
                  selectedCluster: cluster.id
                })
              }
              className="cluster-count-bar"
            />

            {/* Highlighted bar for search matches */}
            <rect
              x={paddingLeft}
              y={0}
              width={highlightBarWidth}
              height={20}
              fill={'gold'}
              opacity={
                documentResults &&
                (!matching_docs_in_cluster ||
                  matching_docs_in_cluster.length === 0)
                  ? 0.2
                  : 1
              }
              onClick={(ev) =>
                dispatch({
                  type: 'SET_SELECTED_CLUSTER',
                  selectedCluster: cluster.id
                })
              }
            />

            {/* Counts */}
            <text
              x={paddingLeft + clusterBarLength + 5}
              y={20}
              fontWeight={selectedCluster === cluster.id ? 'bold' : 'normal'}
              fill={
                documentResults &&
                (!matching_docs_in_cluster ||
                  matching_docs_in_cluster.length === 0)
                  ? 'grey'
                  : 'black'
              }
            >
              {documentResults
                ? `${matching_docs_in_cluster.length}/${cluster.members.length}`
                : `${cluster.members.length}`}
            </text>

            {/* Cluster keywords */}
            <text
              x={clusterBarLength + paddingLeft + 60}
              y={20}
              fontWeight={selectedCluster === cluster.id ? 'bold' : 'normal'}
              fontSize={mousedOverCluster === cluster.id ? '15pt' : '13pt'}
              fontStyle={mousedOverCluster === cluster.id ? 'italic' : 'normal'}
              fill={
                selectedDocument &&
                documentCluster[selectedDocument.id] === cluster.id
                  ? 'gold'
                  : documentResults &&
                    (!matching_docs_in_cluster ||
                      matching_docs_in_cluster.length === 0)
                  ? 'grey'
                  : 'black'
              }
              className="cluster-keywords keywords"
            >
              {cluster.is_outlier ? (
                'Not assigned to cluster'
              ) : (
                <>{clusterLabels[cluster.id]}</>
              )}
            </text>
          </g>
        </svg>
      </h3>

      {isExpandedState[cluster.id] && (
        <ol>
          {doc_ids
            .filter((doc_id) => idsInTable.includes(+doc_id))
            .map((doc_id, i) => {
              const document = allDocuments.filter((d) => d.id === doc_id)[0];

              if (!document) {
                console.log('FAILED TO FIND DOCUMENT WITH ID:', doc_id);
              }

              return (
                <DocumentLi
                  document={document}
                  highlightSnippet={highlightSnippet}
                  key={doc_id}
                  documentNumber={i + 1}
                />
              );
            })}
        </ol>
      )}
    </React.Fragment>
  );
};
Cluster.propTypes = {
  idsInTable: PropTypes.arrayOf(PropTypes.number).isRequired,
  cluster: PropTypes.shape({
    id: PropTypes.number,
    is_outlier: PropTypes.bool,
    index: PropTypes.number,
    members: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        x: PropTypes.number,
        y: PropTypes.number,
        document_id: PropTypes.number
      })
    )
  }).isRequired,
  highlightSnippet: PropTypes.func.isRequired,
  resultDocumentIds: PropTypes.arrayOf(PropTypes.number),
  barX: PropTypes.func,
  clusterBarLength: PropTypes.number
};

const ClusterList = ({ documentsInTable, highlightSnippet }) => {
  let [{ clusterDataCopy: clusterData, documentResults }] = useAppState();
  let [combinedCollapsedState, setCombinedCollapsedState] = useState(
    'collapsed'
  );

  let [
    {
      combinedDocumentCollapsedState,
      setCombinedDocumentCollapsedState,
      isExpandedState
    },
    dispatch
  ] = useAppState();

  if (!clusterData) {
    return null;
  }

  clusterData.sort((a, b) => {
    if (b.is_outlier) {
      return -1;
    } else if (a.is_outlier) {
      return +1;
    } else {
      return b.members.length - a.members.length;
    }
  });

  const clusterIds = clusterData.map((cluster) => cluster.id);

  const idsInTable = documentsInTable.map((d) => d.id);
  if (idsInTable.length === 0) {
    return <p>No matching documents.</p>;
  }

  let resultDocumentIds = [];

  if (documentResults) {
    resultDocumentIds = documentResults.map((d) => +d.id);
  }

  const clusterBarLength = 75;

  const barX = d3
    .scaleLinear()
    .domain([0, d3.max(clusterData.map((c) => c.members.length))])
    .range([0, clusterBarLength]);

  if (!isExpandedState) {
    let isExpanded = [];
    for (let i = 0; i < clusterIds.length; i++) {
      const cluster_id = clusterIds[i];
      isExpanded[cluster_id] = false;
    }
    dispatch({ type: 'SET_IS_EXPANDED_STATE', isExpandedState: isExpanded });
  }

  const setAllExpanded = (value) => {
    let isExpanded = isExpandedState;
    for (let i = 0; i < clusterIds.length; i++) {
      const cluster_id = clusterIds[i];
      isExpanded[cluster_id] = value;
    }
    dispatch({ type: 'SET_IS_EXPANDED_STATE', isExpandedState: isExpanded });
  };
  const CombinedCollapseControl = () => {
    if (combinedCollapsedState === 'collapsed') {
      return (
        <p>
          <Button
            variant="outline-secondary"
            onClick={(ev) => {
              setAllExpanded(true);
              setCombinedCollapsedState('expanded');
            }}
          >
            <BsCaretRightFill />
            Expand all clusters
          </Button>
        </p>
      );
    } else {
      return (
        <p>
          <Button
            variant="outline-secondary"
            onClick={(ev) => {
              setAllExpanded(false);
              setCombinedCollapsedState('collapsed');
            }}
          >
            <BsCaretDownFill />
            Collapse all clusters
          </Button>
        </p>
      );
    }
  };

  const setAllDocumentsExpanded = (value) => {
    let isExpanded = [];
    for (let i = 0; i < documentResults.length; i++) {
      isExpanded[documentResults[i].id] = value;
    }
    dispatch({
      type: 'SET_IS_EXPANDED_DOCUMENT_STATE',
      isExpandedDocumentState: isExpanded
    });
  };
  const CombinedCollapseDocumentControl = () => {
    if (combinedDocumentCollapsedState === 'collapsed') {
      return (
        <p>
          <Button
            variant="outline-secondary"
            onClick={(ev) => {
              setAllDocumentsExpanded(true);
              setCombinedDocumentCollapsedState('expanded');
            }}
            style={{ marginLeft: '5px' }}
          >
            <BsCaretRightFill />
            Expand all documents
          </Button>
        </p>
      );
    } else {
      return (
        <p>
          <Button
            variant="outline-secondary"
            onClick={(ev) => {
              setAllDocumentsExpanded(false);
              setCombinedDocumentCollapsedState('collapsed');
            }}
            style={{ marginLeft: '5px' }}
          >
            <BsCaretDownFill />
            Collapse all documents
          </Button>
        </p>
      );
    }
  };

  const ClusterHeader = () => {
    const paddingLeft = 40;

    return (
      <React.Fragment>
        <h3
          style={{
            fontWeight: 'bold',
            fontSize: '13pt',
            textAnchor: 'start',
            borderBottom: 'solid 2px black'
          }}
        >
          <svg width={clusterBarLength + paddingLeft + 600} height={30}>
            <g transform={'translate(30, 0)'}>
              <text x={-30} y={20}>
                Color
              </text>

              {/* Bar for total count */}
              <text x={paddingLeft} y={20}>
                Num. of docs
              </text>

              {/* Cluster keywords */}
              <text x={clusterBarLength + paddingLeft + 60} y={20}>
                Keywords
              </text>
            </g>
          </svg>
        </h3>
      </React.Fragment>
    );
  };

  if (!isExpandedState) {
    return null;
  }

  return (
    <>
      <Form
        style={{ marginTop: '10px', marginLeft: '13px' }}
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <Form.Group as={Row} controlId="search">
          <CombinedCollapseControl />
          {documentResults && <CombinedCollapseDocumentControl />}
        </Form.Group>
      </Form>

      <ClusterHeader />
      {clusterData.map((cluster, i) => (
        <Cluster
          idsInTable={idsInTable}
          cluster={cluster}
          highlightSnippet={highlightSnippet}
          key={cluster.id}
          resultDocumentIds={resultDocumentIds}
          barX={barX}
          clusterBarLength={clusterBarLength}
        />
      ))}
    </>
  );
};

ClusterList.propTypes = {
  documentsInTable: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number })
  ),
  highlightSnippet: PropTypes.func
};
export default ClusterList;
